import { useState } from "react";

/**
 * 1NOW-11132, Design A: "Clear filters" for the grids that keep their filters
 * in separate pieces of state (class, make, model, price).
 *
 * `filtersActive` is what the "No vehicles match your filters" message waits
 * for. The grids derive their list in an effect and start the price range at
 * [0, 0] until the fleet's top price is known, so for a moment after the cars
 * arrive the list is empty and the range looks narrowed. Price counts as a
 * filter only once the renter has moved it, so that moment is not announced as
 * "no match".
 *
 * `filtersKey` remounts the filter bar on Clear: it keeps draft copies of the
 * selections for its modal and would otherwise show the old ones.
 */
export const useFilterReset = ({
  selectedTypes,
  selectedMakes,
  selectedModels,
  setSelectedTypes,
  setSelectedMakes,
  setSelectedModels,
  setRangePrices,
  maxPrice,
}: {
  selectedTypes: string[];
  selectedMakes: string[];
  selectedModels: string[];
  setSelectedTypes: (v: string[]) => void;
  setSelectedMakes: (v: string[]) => void;
  setSelectedModels: (v: string[]) => void;
  setRangePrices: (v: number[]) => void;
  maxPrice: number;
}) => {
  const [priceTouched, setPriceTouched] = useState(false);
  const [filtersKey, setFiltersKey] = useState(0);

  const pickPrices = (prices: number[]) => {
    setPriceTouched(true);
    setRangePrices(prices);
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedMakes([]);
    setSelectedModels([]);
    setPriceTouched(false);
    setRangePrices([0, maxPrice]);
    setFiltersKey((k) => k + 1);
  };

  const filtersActive =
    selectedTypes.length > 0 ||
    selectedMakes.length > 0 ||
    selectedModels.length > 0 ||
    priceTouched;

  return { filtersActive, filtersKey, pickPrices, clearFilters };
};
